import { useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { fH, fN, DeptBadge, UtilBar, MONTH_NAMES } from './utils';

// Rows come from rpc('direct_reports_weekly'): one per (week_start, cor_user_id), scoped server-side
// to the signed-in leader's direct reports (people.leader_email).

const parseDay = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const d = parseDay(s); d.setDate(d.getDate() + n); return d; };
const dayLabel = d => `${MONTH_NAMES[d.getMonth() + 1]} ${d.getDate()}`;
const weekLabel = ws => `${dayLabel(parseDay(ws))} – ${dayLabel(addDays(ws, 6))}`;
const num = v => Number(v) || 0;
const utilPct = r => (num(r.capacity_hours) > 0 ? num(r.total_hours) / num(r.capacity_hours) * 100 : 0);

export default function DirectReportsTab({ rows, session }) {
  const weeks = useMemo(() => [...new Set(rows.map(r => r.week_start))].sort().reverse(), [rows]);

  // Default to the last fully finished week (the Monday report covers the week before).
  const defaultWeek = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return weeks.find(ws => addDays(ws, 6) < today) || weeks[0];
  }, [weeks]);

  const [selWeek,  setSelWeek]  = useState(defaultWeek);
  const [expanded, setExpanded] = useState(null);
  const [comments, setComments] = useState([]);

  useEffect(() => { setSelWeek(defaultWeek); }, [defaultWeek]);

  const loadComments = useCallback(async () => {
    const { data } = await supabase.from('report_comments').select('*').order('created_at', { ascending: true });
    setComments(data || []);
  }, []);
  useEffect(() => { loadComments(); }, [loadComments]);

  const prevWeek = weeks[weeks.indexOf(selWeek) + 1];
  const cur  = useMemo(() => rows.filter(r => r.week_start === selWeek), [rows, selWeek]);
  const prev = useMemo(() => rows.filter(r => r.week_start === prevWeek), [rows, prevWeek]);
  const prevByUser = useMemo(() => Object.fromEntries(prev.map(r => [r.cor_user_id, r])), [prev]);

  // Last 8 weeks of hours per person, oldest first, for the trend bars.
  const trendByUser = useMemo(() => {
    const last8 = weeks.slice(0, 8).reverse();
    const m = {};
    for (const r of rows) {
      const i = last8.indexOf(r.week_start);
      if (i < 0) continue;
      (m[r.cor_user_id] ||= Array(last8.length).fill(null))[i] = { ws: r.week_start, h: num(r.total_hours), cap: num(r.capacity_hours) };
    }
    return m;
  }, [rows, weeks]);

  const totals = useMemo(() => {
    const sum = (list, k) => list.reduce((a, r) => a + num(r[k]), 0);
    return {
      hours: sum(cur, 'total_hours'), prevHours: sum(prev, 'total_hours'),
      billable: sum(cur, 'billable_hours'), internal: sum(cur, 'internal_hours'),
      pto: sum(cur, 'pto_hours'), capacity: sum(cur, 'capacity_hours'),
    };
  }, [cur, prev]);

  const projects = useMemo(() => rollupProjects(cur, prev), [cur, prev]);
  const people = useMemo(() => [...cur].sort((a, b) => num(b.total_hours) - num(a.total_hours)), [cur]);

  const weekComments = comments.filter(c => c.week_start === selWeek);

  if (!selWeek) return <Empty text="No direct reports found for your account." />;

  return (
    <>
      {/* ── Week picker + export ── */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <select value={selWeek} onChange={e => { setSelWeek(e.target.value); setExpanded(null); }} style={selectStyle}>
          {weeks.map(ws => <option key={ws} value={ws}>Week of {weekLabel(ws)}</option>)}
        </select>
        <span style={{ fontSize:12, color:'var(--text3)' }}>
          {cur.length} direct reports · live from COR hours{prevWeek ? ` · Δ vs ${weekLabel(prevWeek)}` : ''}
        </span>
        <button
          onClick={() => downloadMarkdown(selWeek, people, projects, weekComments, totals)}
          style={{ ...btnStyle, marginLeft:'auto' }}
        >Export week (.md)</button>
      </div>

      {/* ── KPIs ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
        <Kpi label="Hours logged" value={fH(totals.hours)} sub={<Delta cur={totals.hours} prev={prev.length ? totals.prevHours : null} />} color="var(--text)" />
        <Kpi label="Utilization" value={totals.capacity ? `${fN(totals.hours / totals.capacity * 100, 0)}%` : '—'} sub={`of ${fH(totals.capacity)}h capacity`} color="var(--purple)" />
        <Kpi label="Client work" value={totals.hours ? `${fN(totals.billable / totals.hours * 100, 0)}%` : '—'} sub={`${fH(totals.billable)}h on clients`} color="var(--green)" />
        <Kpi label="Internal" value={fH(totals.internal)} sub="INPULSE [Internal] hours" color="var(--amber)" />
        <Kpi label="PTO" value={fH(totals.pto)} sub="hours off this week" color="var(--blue)" />
        <Kpi label="Projects" value={projects.length} sub="with hours this week" color="var(--text)" />
      </div>

      {/* ── People ── */}
      <Section title="Team">
        <table style={tableStyle}>
          <thead>
            <tr>
              {['Person', 'Hours', 'Clients', 'Internal', 'PTO', 'Utilization', 'Main projects', 'Last 8 weeks', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {people.map(r => {
              const open = expanded === r.cor_user_id;
              const nNotes = comments.filter(c => c.cor_user_id === r.cor_user_id).length;
              return [
                <tr key={r.cor_user_id} onClick={() => setExpanded(open ? null : r.cor_user_id)} style={{ cursor:'pointer', background: open ? 'var(--card2)' : undefined }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight:600 }}>{r.person}</div>
                    <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:3 }}>
                      <DeptBadge dept={r.department || 'Unassigned'} />
                      <span style={{ fontSize:11, color:'var(--text3)' }}>{r.position}</span>
                    </div>
                  </td>
                  <td style={tdMono}>
                    {fH(num(r.total_hours))}
                    <div style={{ fontSize:11 }}><Delta cur={num(r.total_hours)} prev={prevByUser[r.cor_user_id] ? num(prevByUser[r.cor_user_id].total_hours) : null} /></div>
                  </td>
                  <td style={tdMono}>{fH(num(r.billable_hours))}</td>
                  <td style={tdMono}>{fH(num(r.internal_hours))}</td>
                  <td style={tdMono}>{num(r.pto_hours) ? fH(num(r.pto_hours)) : '—'}</td>
                  <td style={tdStyle}>
                    <UtilBar pct={utilPct(r)} />
                    <div style={{ fontSize:10, color:'var(--text3)', marginTop:2 }}>of {fH(num(r.capacity_hours))}h</div>
                  </td>
                  <td style={{ ...tdStyle, maxWidth:320 }}>
                    {r.by_project.length === 0
                      ? <span style={{ color:'var(--red)', fontSize:12 }}>No hours logged</span>
                      : r.by_project.slice(0, 3).map(p => <ProjectChip key={`${p.client}|${p.project}`} p={p} />)}
                    {r.by_project.length > 3 && <span style={{ fontSize:11, color:'var(--text3)' }}> +{r.by_project.length - 3}</span>}
                  </td>
                  <td style={tdStyle}><Trend points={trendByUser[r.cor_user_id] || []} selWeek={selWeek} /></td>
                  <td style={{ ...tdStyle, fontSize:11, color:'var(--text3)', whiteSpace:'nowrap' }}>{nNotes ? `💬 ${nNotes}` : ''} {open ? '▲' : '▼'}</td>
                </tr>,
                open && (
                  <tr key={`${r.cor_user_id}-detail`}>
                    <td colSpan={9} style={{ padding:'14px 18px 18px', background:'var(--card2)', borderBottom:'1px solid var(--border)' }}>
                      <PersonDetail
                        row={r} selWeek={selWeek} session={session}
                        notes={comments.filter(c => c.cor_user_id === r.cor_user_id)}
                        onChange={loadComments}
                      />
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </Section>

      {/* ── Projects ── */}
      <Section title="Projects this week" sub="Hours across your direct reports. Task completion and due-date KPIs arrive with the COR task sync.">
        <table style={tableStyle}>
          <thead>
            <tr>{['Client', 'Project', 'Hours', 'Δ vs prior week', 'People'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <tr key={`${p.client}|${p.project}`}>
                <td style={tdStyle}>{p.client}</td>
                <td style={{ ...tdStyle, fontWeight:500 }}>{p.project}</td>
                <td style={tdMono}>{fH(p.hours)}</td>
                <td style={tdMono}><Delta cur={p.hours} prev={prevWeek ? p.prevHours : null} /></td>
                <td style={{ ...tdStyle, fontSize:12, color:'var(--text2)' }}>{p.people.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* ── General notes for the week ── */}
      <Section title="Notes for this week" sub="Only you can see your notes." padded>
        <Notes notes={weekComments.filter(c => c.cor_user_id == null)} selWeek={selWeek} corUserId={null} session={session} onChange={loadComments} />
      </Section>
    </>
  );
}

function rollupProjects(cur, prev) {
  const m = {};
  const key = p => `${p.client}|${p.project}`;
  for (const r of cur) for (const p of r.by_project) {
    const e = (m[key(p)] ||= { client: p.client, project: p.project, hours: 0, prevHours: 0, people: [] });
    e.hours += num(p.hours);
    e.people.push(r.person);
  }
  for (const r of prev) for (const p of r.by_project) {
    if (m[key(p)]) m[key(p)].prevHours += num(p.hours);
  }
  return Object.values(m).sort((a, b) => b.hours - a.hours);
}

function PersonDetail({ row, selWeek, session, notes, onChange }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'minmax(280px,1fr) minmax(320px,1.2fr)', gap:24 }}>
      <div>
        <div style={subHead}>All projects · {weekLabel(selWeek)}</div>
        {row.by_project.length === 0 && <div style={{ fontSize:12, color:'var(--text3)' }}>No hours logged this week.</div>}
        {row.by_project.map(p => (
          <div key={`${p.client}|${p.project}`} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'5px 0', borderBottom:'1px dashed var(--border)', fontSize:12 }}>
            <span><span style={{ color:'var(--text3)' }}>{p.client} · </span>{p.project}</span>
            <span style={{ fontFamily:'DM Mono,monospace' }}>{fH(num(p.hours))}h</span>
          </div>
        ))}
      </div>
      <div>
        <div style={subHead}>Notes on {row.person.split(' ')[0]}</div>
        <Notes notes={notes} selWeek={selWeek} corUserId={row.cor_user_id} session={session} onChange={onChange} showHistory />
      </div>
    </div>
  );
}

// Shows this week's notes (editable) plus, optionally, earlier weeks' notes read-only, newest first.
function Notes({ notes, selWeek, corUserId, session, onChange, showHistory = false }) {
  const [draft,   setDraft]   = useState('');
  const [editId,  setEditId]  = useState(null);
  const [editTxt, setEditTxt] = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState(null);

  const thisWeek = notes.filter(n => n.week_start === selWeek);
  const history  = showHistory ? notes.filter(n => n.week_start !== selWeek).sort((a, b) => b.week_start.localeCompare(a.week_start)) : [];

  const run = async fn => {
    setBusy(true); setError(null);
    const { error } = await fn();
    setBusy(false);
    if (error) { setError(error.message); return false; }
    await onChange();
    return true;
  };

  const add = async () => {
    if (!draft.trim()) return;
    const ok = await run(() => supabase.from('report_comments').insert({
      leader_email: session.user.email, week_start: selWeek, cor_user_id: corUserId, body: draft.trim(),
    }));
    if (ok) setDraft('');
  };
  const save = async id => {
    const ok = await run(() => supabase.from('report_comments').update({ body: editTxt.trim(), updated_at: new Date().toISOString() }).eq('id', id));
    if (ok) setEditId(null);
  };
  const remove = id => {
    if (window.confirm('Delete this note?')) run(() => supabase.from('report_comments').delete().eq('id', id));
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      {thisWeek.map(n => (
        <div key={n.id} style={noteStyle}>
          {editId === n.id ? (
            <>
              <textarea value={editTxt} onChange={e => setEditTxt(e.target.value)} rows={3} style={textareaStyle} />
              <div style={{ display:'flex', gap:8, marginTop:6 }}>
                <button disabled={busy} onClick={() => save(n.id)} style={btnPrimary}>Save</button>
                <button onClick={() => setEditId(null)} style={btnStyle}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ whiteSpace:'pre-wrap', fontSize:13 }}>{n.body}</div>
              <div style={{ display:'flex', gap:10, marginTop:6, fontSize:11, color:'var(--text3)' }}>
                <span>{new Date(n.created_at).toLocaleDateString()}</span>
                <button onClick={() => { setEditId(n.id); setEditTxt(n.body); }} style={linkBtn}>Edit</button>
                <button onClick={() => remove(n.id)} style={{ ...linkBtn, color:'var(--red)' }}>Delete</button>
              </div>
            </>
          )}
        </div>
      ))}
      <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} placeholder="Add a note for this week…" style={textareaStyle} />
      <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
        <button disabled={busy || !draft.trim()} onClick={add} style={btnPrimary}>Add note</button>
        {error && <span style={{ fontSize:12, color:'var(--red)' }}>{error}</span>}
      </div>
      {history.length > 0 && (
        <div style={{ marginTop:14 }}>
          <div style={{ ...subHead, marginBottom:6 }}>Earlier notes</div>
          {history.map(n => (
            <div key={n.id} style={{ ...noteStyle, background:'transparent' }}>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:3 }}>Week of {weekLabel(n.week_start)}</div>
              <div style={{ whiteSpace:'pre-wrap', fontSize:12, color:'var(--text2)' }}>{n.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Trend({ points, selWeek }) {
  const max = Math.max(1, ...points.map(p => (p ? Math.max(p.h, p.cap) : 0)));
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:26 }}>
      {points.map((p, i) => (
        <div
          key={i}
          title={p ? `${weekLabel(p.ws)}: ${fH(p.h)}h of ${fH(p.cap)}h` : ''}
          style={{
            width:7, borderRadius:2,
            height: p ? Math.max(2, p.h / max * 26) : 2,
            background: !p ? 'var(--border)' : p.ws === selWeek ? 'var(--green)' : 'var(--bar-idle)',
          }}
        />
      ))}
    </div>
  );
}

function Delta({ cur, prev }) {
  if (prev == null) return <span style={{ color:'var(--text3)' }}>—</span>;
  const d = cur - prev;
  if (Math.abs(d) < 0.05) return <span style={{ color:'var(--text3)' }}>±0</span>;
  return <span style={{ color: d > 0 ? 'var(--green)' : 'var(--red)' }}>{d > 0 ? '+' : ''}{fH(d)}h</span>;
}

function ProjectChip({ p }) {
  return (
    <span title={`${p.client} · ${p.project}`} style={{
      display:'inline-block', margin:'2px 4px 2px 0', padding:'2px 7px', borderRadius:4, fontSize:11,
      background:'var(--blue-bg)', color:'var(--blue)', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', verticalAlign:'middle',
    }}>{p.project} <b style={{ fontFamily:'DM Mono,monospace' }}>{fH(num(p.hours))}</b></span>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
      <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.8px', color:'var(--text2)', marginBottom:6, fontWeight:500 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, fontFamily:'DM Mono,monospace', color, lineHeight:1.1 }}>{value}</div>
      <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>{sub}</div>
    </div>
  );
}

function Section({ title, sub, padded = false, children }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, marginBottom:20, overflow:'hidden' }}>
      <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)' }}>
        <div style={{ fontSize:14, fontWeight:700 }}>{title}</div>
        {sub && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{sub}</div>}
      </div>
      <div style={{ overflowX:'auto', padding: padded ? 18 : 0 }}>{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ padding:60, textAlign:'center', color:'var(--text3)' }}>{text}</div>;
}

function downloadMarkdown(selWeek, people, projects, weekComments, totals) {
  const notesFor = id => weekComments.filter(c => c.cor_user_id === id).map(c => `  - 💬 ${c.body.replace(/\n/g, ' ')}`);
  const lines = [
    `# Direct Reports — week of ${weekLabel(selWeek)}`,
    '',
    `**${fH(totals.hours)}h** logged · ${totals.capacity ? fN(totals.hours / totals.capacity * 100, 0) : 0}% of capacity · ${fH(totals.billable)}h client · ${fH(totals.internal)}h internal · ${fH(totals.pto)}h PTO`,
    '',
    '## Team',
    ...people.flatMap(r => [
      `- **${r.person}** (${r.department || '—'}) — ${fH(num(r.total_hours))}h, ${fN(utilPct(r), 0)}% of ${fH(num(r.capacity_hours))}h`
        + (r.by_project.length ? `: ${r.by_project.slice(0, 5).map(p => `${p.project} ${fH(num(p.hours))}h`).join(', ')}` : ': no hours logged'),
      ...notesFor(r.cor_user_id),
    ]),
    '',
    '## Projects',
    ...projects.map(p => `- ${p.client} · ${p.project}: ${fH(p.hours)}h (${p.people.join(', ')})`),
  ];
  const general = weekComments.filter(c => c.cor_user_id == null);
  if (general.length) lines.push('', '## Notes', ...general.map(c => `- ${c.body.replace(/\n/g, ' ')}`));

  const blob = new Blob([lines.join('\n') + '\n'], { type:'text/markdown' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `direct-reports-${selWeek}.md`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const selectStyle = {
  background:'var(--card)', border:'1px solid var(--border)', borderRadius:8,
  padding:'8px 14px', color:'var(--text)', fontSize:13, cursor:'pointer', fontWeight:600,
};
const btnStyle = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontSize:12, fontWeight:600, color:'var(--text)' };
const btnPrimary = { ...btnStyle, background:'var(--green)', borderColor:'var(--green)', color:'var(--on-green)' };
const linkBtn = { background:'none', border:'none', padding:0, cursor:'pointer', fontSize:11, color:'var(--text2)' };
const tableStyle = { width:'100%', borderCollapse:'collapse', fontSize:13 };
const thStyle = { textAlign:'left', padding:'10px 14px', fontSize:10, textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', fontWeight:600, borderBottom:'1px solid var(--border)', background:'var(--card2)', whiteSpace:'nowrap' };
const tdStyle = { padding:'10px 14px', borderBottom:'1px solid var(--border)', verticalAlign:'top' };
const tdMono = { ...tdStyle, fontFamily:'DM Mono,monospace', whiteSpace:'nowrap' };
const subHead = { fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text2)', marginBottom:8 };
const noteStyle = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px', marginBottom:8 };
const textareaStyle = { width:'100%', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px', fontSize:13, fontFamily:'inherit', resize:'vertical', background:'var(--card2)', color:'var(--text)' };
